import { selectCountries } from "../../../../components/appData.slice";
import { FormElement, FormId } from "../../../../components/enums";
import {
  ComplexFormSchemaComponents,
  FormSchema,
  SimpleFormSchemaComponents,
} from "../../../../components/types";
import { provinces, insuranceCompanies, makes } from "../../../../shared";
import {
  selectCountry,
  selectIsDefaultCountrySelected,
  selectIsNotDefaultCountrySelected,
} from "../../../../store/HOC/genericUI.selectors";
import { postalCodeMask } from "../../../../utilities/masks";
import {
  selectIsCarOwnerDefined,
  selectIsCompanyNotOwner,
  selectPerpetratorOwnerData,
  selectUIData,
  selectOwnerPerpetratorInsuranceCompanyValue,
  selectPerpetratorAvailableCities,
  selectPerpetratorAvailableModels,
} from "../perpetrator.selectors";
import {
  setUIField,
  setUIObjectField,
  setInsuranceCompany,
  setAvailableCities,
  setAvailableModels,
} from "../perpetrator.slice";

import { ComponentId } from "./enums";

export const owner: FormSchema<
  ComplexFormSchemaComponents | SimpleFormSchemaComponents
> = {
  formId: FormId.PERPETRATOR_OWNER,
  id: [FormId.PERPETRATOR_OWNER, "owner"].join("-"),
  selector: selectPerpetratorOwnerData,
  isVisible: selectIsCompanyNotOwner,
  content: [
    {
      sections: [
        {
          title: "Dane właściciela pojazdu",
          id: "perpetrator-owner-details-1",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_NAME,
              label: "Imię",
            },
            {
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_SURNAME,
              label: "Nazwisko",
            },
          ],
        },
        {
          id: "perpetrator-owner-details-2",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.PHONE,
              country: selectCountry(selectUIData)("ownerphone"),
              onChange: setUIObjectField,
              id: ComponentId.PERPETRATOR_OWNER_PHONE,
              label: "Telefon",
            },
            {
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_EMAIL,
              label: "Email",
            },
          ],
        },
      ],
    },
  ],
};

export const address: FormSchema<
  ComplexFormSchemaComponents | SimpleFormSchemaComponents
> = {
  formId: FormId.PERPETRATOR_OWNER,
  id: [FormId.PERPETRATOR_OWNER, "address"].join("-"),
  selector: selectPerpetratorOwnerData,
  isVisible: selectIsCompanyNotOwner,
  content: [
    {
      sections: [
        {
          title: "Miejsce zamieszkania",
          id: "perpetrator-owner-details-3",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_STREET,
              label: "Ulica",
            },
            {
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_STREET_2,
              label: "Numer budynku / mieszkania",
            },
          ],
        },
        {
          id: "perpetrator-owner-details-4",
          wrapper: { xs: 12 },
          components: [
            {
              isVisible: selectIsNotDefaultCountrySelected(selectUIData)(
                "companyData[country]"
              ),
              type: FormElement.ZIP_CODE,
              mask: postalCodeMask,
              id: ComponentId.PERPETRATOR_OWNER_ZIP_CODE_PL,
              label: "Kod pocztowy",
              // LPPLWEBMOB-728
              onChange: setAvailableCities,
            },
            {
              isVisible: selectIsDefaultCountrySelected(selectUIData)(
                "companyData[country]"
              ),
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_ZIP_CODE_GENERIC,
              label: "Kod pocztowy",
            },
            // LPPLWEBMOB-728
            {
              isVisible: selectIsNotDefaultCountrySelected(selectUIData)(
                "circumstances[country]"
              ),
              type: FormElement.SELECT_CITY,
              id: ComponentId.PERPETRATOR_OWNER_CITY,
              label: "Miejscowość",
              items: [],
              selectAvailableCities: selectPerpetratorAvailableCities,
            },
            {
              isVisible: selectIsDefaultCountrySelected(selectUIData)(
                "circumstances[country]"
              ),
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_CITY,
              label: "Miejscowość",
            },
          ],
        },
        {
          id: "perpetrator-owner-details-5",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.AUTOCOMPLETE,
              id: ComponentId.PERPETRATOR_OWNER_COUNTRY,
              items: selectCountries,
              onChange: setUIField,
              label: "Kraj",
            },
            {
              type: FormElement.SELECT,
              id: ComponentId.PERPETRATOR_OWNER_PROVINCE,
              disabled:
                selectIsDefaultCountrySelected(selectUIData)("owner[country]"),
              label: "Województwo",
              items: [...provinces],
            },
          ],
        },
      ],
    },
  ],
};

export const car: FormSchema<
  ComplexFormSchemaComponents | SimpleFormSchemaComponents
> = {
  formId: FormId.PERPETRATOR_OWNER,
  id: [FormId.PERPETRATOR_OWNER, "car"].join("-"),
  selector: selectPerpetratorOwnerData,
  isVisible: selectIsCarOwnerDefined,
  content: [
    {
      sections: [
        {
          title: "Dane pojazdu",
          id: "perpetrator-owner-car-1",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_REGISTRATION_NUMBER,
              label: "Numer rejestracyjny  pojazdu",
              required: true,
            },
          ],
        },
        {
          id: "perpetrator-owner-car-2",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.SELECT_MAKE,
              id: ComponentId.PERPETRATOR_OWNER_MAKE,
              label: "Marka",
              onChange: setAvailableModels,
              items: [...makes],
            },
            {
              type: FormElement.SELECT_MODEL,
              id: ComponentId.PERPETRATOR_OWNER_MODEL,
              label: "Model",
              items: [],
              selectAvailableModels: selectPerpetratorAvailableModels,
            },
          ],
        },
        {
          title: "Dane polisy",
          id: "perpetrator-owner-car-insurance",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_OC_NUMBER,
              label: "Numer polisy OC",
              required: true,
            },
            {
              type: FormElement.SELECT,
              id: ComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY,
              label: "Towarzystwo ubezpieczeniowe",
              onChange: setInsuranceCompany,
              items: [...insuranceCompanies],
              required: true,
            },
            {
              isVisible: selectOwnerPerpetratorInsuranceCompanyValue,
              type: FormElement.INPUT,
              id: ComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY,
              label: "Inny ubezpieczyciel",
              required: true,
            },
          ],
        },
      ],
    },
  ],
};

export const documents: FormSchema<
  ComplexFormSchemaComponents | SimpleFormSchemaComponents
> = {
  formId: FormId.PERPETRATOR_OWNER,
  id: [FormId.PERPETRATOR_OWNER, "documents"].join("-"),
  selector: selectPerpetratorOwnerData,
  isVisible: selectIsCompanyNotOwner,
  content: [
    {
      sections: [
        {
          title: "Zdjęcia dokumentów",
          id: "perpetrator-owner-documents",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.IMAGE_UPLOADER_MULTIPLE,
              id: ComponentId.PERPETRATOR_OWNER_DOCUMENTS,
              maxImageNumber: 10,
              imageSectionId: ComponentId.PERPETRATOR_OWNER_DOCUMENTS,
            },
          ],
        },
      ],
    },
  ],
};
